import React, { useEffect, useState } from "react";
import { Button, Switch } from "@mui/material";
import ProgressBar from "../../UI/ProgressBar";
import { useAlert } from "../../../hooks/useAlert";
import {
  useGetPromotionEmailSettingsQuery,
  useResetPromotionEmailSettingsMutation,
  useUpdatePromotionEmailSettingsMutation,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import {
  CANCELLATION_VARIABLES,
  DEFAULT_RSVP_SCHEDULE,
  INVITE_COPY_VARIABLES,
  SETTINGS_SUBJECT_LIMIT,
} from "./emailSettingsDisplay";
import { VariableTextField } from "./VariableTextField";
import { TriggerTimingField } from "./TriggerTimingField";

const NAV = [
  { value: "cancellation", label: "Cancellation" },
  { value: "reminder", label: "Reminder" },
];

const SettingsSwitch = ({ checked, onChange, disabled }) => (
  <Switch
    className="email-settings-switch"
    checked={checked}
    onChange={(e) => onChange(e.target.checked)}
    disabled={disabled}
    disableRipple
  />
);

const ToggleRow = ({ title, copy, checked, onChange, disabled }) => (
  <div className={`email-settings-toggle${disabled ? " is-disabled" : ""}`}>
    <div className="email-settings-toggle__head">
      <span>{title}</span>
      {copy ? null : (
        <SettingsSwitch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
      )}
    </div>
    {copy ? (
      <div className="email-settings-toggle__control">
        <p>{copy}</p>
        <SettingsSwitch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
      </div>
    ) : null}
  </div>
);

const emptyRsvp = () => ({
  subject: "",
  schedule: { ...DEFAULT_RSVP_SCHEDULE },
});

const toRsvpSchedulePayload = (schedule = {}) => ({
  ...DEFAULT_RSVP_SCHEDULE,
  ...schedule,
  triggerEnabled: true,
  repeats: [],
  cutoffEnabled: false,
});

export const EmailSettingsPanel = () => {
  const { displayAlert } = useAlert();
  const [nav, setNav] = useState("cancellation");
  const [cancellation, setCancellation] = useState({
    subject: "",
    message: "",
  });
  const [rsvp, setRsvp] = useState(emptyRsvp);
  const senderFallback = "quest.servers@qgroup.se";

  const { data, isLoading, isFetching, error } =
    useGetPromotionEmailSettingsQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  const [saveSettings, { isLoading: isSaving }] =
    useUpdatePromotionEmailSettingsMutation();
  const [resetSettings, { isLoading: isResetting }] =
    useResetPromotionEmailSettingsMutation();

  const settings = data?.data?.settings;
  const senderEmail = settings?.senderEmail || senderFallback;

  useEffect(() => {
    if (!settings) return;
    setCancellation({
      subject: settings.cancellation?.subject || "",
      message: settings.cancellation?.message || "",
    });
    setRsvp({
      subject: settings.rsvp?.subject || "",
      schedule: toRsvpSchedulePayload(settings.rsvp?.schedule),
    });
  }, [settings]);

  const notify = (message, type = "success") =>
    displayAlert({ open: true, message, type, timeout: "2500" });

  const persist = async (section, payload) => {
    await saveSettings({ section, ...payload }).unwrap();
    notify("Settings saved");
  };

  const handleSave = async () => {
    try {
      if (nav === "cancellation") {
        if (!cancellation.subject.trim() || !cancellation.message.trim()) {
          notify("Subject line and message are required", "error");
          return;
        }
        await persist("cancellation", cancellation);
        return;
      }
      if (!rsvp.subject.trim()) {
        notify("Subject line is required", "error");
        return;
      }
      await persist("rsvp", {
        subject: rsvp.subject,
        schedule: toRsvpSchedulePayload(rsvp.schedule),
      });
    } catch (err) {
      notify(
        err?.data?.message || err?.message || "Unable to save settings",
        "error"
      );
    }
  };

  const handleReset = async () => {
    const section = nav === "cancellation" ? "cancellation" : "rsvp";
    try {
      await resetSettings({ section }).unwrap();
      notify("Settings reset to default");
    } catch (err) {
      notify(
        err?.data?.message || err?.message || "Unable to reset settings",
        "error"
      );
    }
  };

  const panelLoading = isLoading || isFetching || isSaving || isResetting;
  const saveLabel = isSaving ? "Saving…" : "Save & Update";

  return (
    <div className="email-settings">
      <aside className="email-settings__nav">
        {NAV.map((item) => (
          <button
            key={item.value}
            type="button"
            className={nav === item.value ? "is-active" : ""}
            onClick={() => setNav(item.value)}
          >
            {item.label}
          </button>
        ))}
      </aside>

      <section className="email-settings__panel">
        {panelLoading ? (
          <div className="email-settings__progress">
            <ProgressBar />
          </div>
        ) : error ? (
          <p className="promotions-table__empty">
            {error?.data?.message || "Unable to load email settings"}
          </p>
        ) : (
          <>
            {nav === "cancellation" && (
              <>
                <label className="email-settings-field">
                  <span>
                    Sender Email <em>*</em>
                  </span>
                  <input type="text" value={senderEmail} disabled />
                </label>
                <VariableTextField
                  label="Subject Line"
                  required
                  value={cancellation.subject}
                  max={SETTINGS_SUBJECT_LIMIT}
                  variables={CANCELLATION_VARIABLES}
                  onChange={(subject) =>
                    setCancellation((prev) => ({ ...prev, subject }))
                  }
                />
                <VariableTextField
                  label="Message"
                  required
                  multiline
                  value={cancellation.message}
                  variables={CANCELLATION_VARIABLES}
                  onChange={(message) =>
                    setCancellation((prev) => ({ ...prev, message }))
                  }
                />
              </>
            )}

            {nav === "reminder" && (
              <>
                <ToggleRow
                  title="Enable RSVP Reminders"
                  copy="Send Reminders To Guests Who Haven't Responded Yet."
                  checked={rsvp.schedule.enabled}
                  onChange={(enabled) =>
                    setRsvp((prev) => ({
                      ...prev,
                      schedule: { ...prev.schedule, enabled },
                    }))
                  }
                />
                <div
                  className={`email-settings-field${
                    !rsvp.schedule.enabled ? " is-disabled" : ""
                  }`}
                >
                  <span>Trigger Timing / Schedule</span>
                  <TriggerTimingField
                    value={rsvp.schedule.trigger}
                    disabled={!rsvp.schedule.enabled}
                    onChange={(trigger) =>
                      setRsvp((prev) => ({
                        ...prev,
                        schedule: {
                          ...prev.schedule,
                          trigger,
                          triggerEnabled: true,
                        },
                      }))
                    }
                  />
                </div>
                <VariableTextField
                  label="Subject Line"
                  required
                  value={rsvp.subject}
                  max={SETTINGS_SUBJECT_LIMIT}
                  variables={INVITE_COPY_VARIABLES}
                  onChange={(subject) =>
                    setRsvp((prev) => ({ ...prev, subject }))
                  }
                />
              </>
            )}

            <div className="email-settings__footer">
              <Button
                variant="outlined"
                className="email-template-studio__reset"
                onClick={handleReset}
                disabled={isResetting}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                disableElevation
                className="email-template-studio__save"
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  backgroundColor: "#3361cf",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#1e4ab6" },
                  "&.Mui-disabled": {
                    backgroundColor: "#8a94a6",
                    color: "#fff",
                  },
                }}
              >
                {saveLabel}
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};
